import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  FileText,
  Download,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  GraduationCap,
  Languages,
  Wrench,
  Eye,
  Building2,
  Trash2,
  CalendarDays,
  Plus,
  UserX,
  IndianRupee,
  BadgeCheck,
  Clock,
} from "lucide-react";
import { STATUS_META, STATUS_OPTIONS } from "@/lib/status";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const InfoRow = ({ label, value }) => (
  <div className="grid grid-cols-3 gap-3 text-sm py-2 border-b border-slate-100 last:border-0">
    <div className="text-ink-600">{label}</div>
    <div className="col-span-2 text-ink-900 font-medium break-words">
      {value || <span className="text-ink-400">—</span>}
    </div>
  </div>
);

export default function CandidateDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [linedUp, setLinedUp] = useState("");
  const [linedUpNote, setLinedUpNote] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [interviews, setInterviews] = useState([]);
  const [ivOpen, setIvOpen] = useState(false);
  const [ivForm, setIvForm] = useState({
    company_name: "",
    role: "",
    date: "",
    time: "",
    location: "",
    mode: "In-person",
    notes: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/candidates/${id}`);
      setC(data);
      setLinedUp(data.lined_up_for || "");
      setLinedUpNote(data.lined_up_note || "");
      setInternalNote(data.internal_note || "");
      const ivRes = await api.get("/interviews", { params: { candidate_id: id } });
      setInterviews(ivRes.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      nav("/candidates");
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/candidates/${id}`, { status });
      setC(data);
      toast.success("Status updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveLinedUp = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/candidates/${id}`, {
        lined_up_for: linedUp,
        lined_up_note: linedUpNote,
      });
      setC(data);
      toast.success("Line-up saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveNote = async () => {
    try {
      const { data } = await api.patch(`/candidates/${id}`, {
        internal_note: internalNote,
      });
      setC(data);
      toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteCandidate = async () => {
    if (!window.confirm("Delete this candidate permanently?")) return;
    try {
      await api.delete(`/candidates/${id}`);
      toast.success("Candidate deleted");
      nav("/candidates");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const scheduleInterview = async () => {
    if (!ivForm.company_name || !ivForm.date || !ivForm.time) {
      toast.error("Company, date and time are required");
      return;
    }
    try {
      const scheduled_at = new Date(
        `${ivForm.date}T${ivForm.time}`
      ).toISOString();
      await api.post("/interviews", {
        candidate_id: id,
        company_name: ivForm.company_name,
        role: ivForm.role || undefined,
        scheduled_at,
        location: ivForm.location || undefined,
        mode: ivForm.mode,
        notes: ivForm.notes || undefined,
      });
      toast.success("Interview scheduled");
      setIvOpen(false);
      setIvForm({
        company_name: "",
        role: "",
        date: "",
        time: "",
        location: "",
        mode: "In-person",
        notes: "",
      });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeInterview = async (iid) => {
    if (!window.confirm("Delete this interview?")) return;
    try {
      await api.delete(`/interviews/${iid}`);
      toast.success("Interview removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleVerifyPayment = async (p) => {
    try {
      const { data } = await api.patch(`/candidates/${id}/payments/verify`, {
        utr: p.utr,
        verified: !p.verified,
      });
      setC(data);
      toast.success(!p.verified ? "Payment marked verified" : "Verification removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadEmployerCopy = async () => {
    if (c.cv_path && c.cv_path.toLowerCase().endsWith(".pdf")) {
      try {
        const res = await api.get(`/candidates/${id}/employer-cv`, {
          responseType: "blob",
        });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(c.full_name || "candidate").replace(/\s+/g, "_")}_EmployerCopy.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Original CV downloaded — phone & email removed");
        return;
      } catch (e) {
        toast.info("Could not redact original CV — generating employer copy instead");
      }
    }
    generateCV({ anonymize: true });
  };

  const generateCV = async (opts = {}) => {
    try {
      const { generateResumePDF } = await import("@/lib/resume");
      const { download } = generateResumePDF(c, opts);
      const suffix = opts.anonymize ? "_EmployerCopy" : "_CV";
      download(
        `${(c.full_name || "candidate").replace(/\s+/g, "_")}${suffix}.pdf`
      );
      toast.success(
        opts.anonymize
          ? "Employer copy downloaded (no phone/email)"
          : "CV PDF downloaded"
      );
    } catch (e) {
      toast.error("Could not generate CV");
    }
  };

  if (!c) {
    return (
      <AppShell>
        <div className="text-sm text-ink-600 animate-pulse">Loading…</div>
      </AppShell>
    );
  }

  const meta = STATUS_META[c.status] || STATUS_META.new;

  return (
    <AppShell>
      <div className="mb-6">
        <button
          type="button"
          data-testid="back-to-candidates"
          onClick={() =>
            window.history.state && window.history.state.idx > 0
              ? nav(-1)
              : nav("/candidates")
          }
          className="inline-flex items-center gap-1 text-sm text-ink-600 hover:text-brand-700"
        >
          <ArrowLeft size={14} /> Back to candidates
        </button>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-6">
          {/* Header card */}
          <Card className="tj-card">
            <CardContent className="p-6">
              <div className="flex items-start gap-5">
                {c.photo_path ? (
                  <img
                    src={fileUrl(c.photo_path)}
                    alt={c.full_name}
                    className="h-20 w-20 rounded-xl object-cover ring-1 ring-slate-200"
                  />
                ) : (
                  <div className="h-20 w-20 shrink-0 rounded-xl bg-brand-100 text-brand-800 grid place-items-center font-heading font-semibold text-3xl">
                    {(c.full_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1
                      data-testid="candidate-name"
                      className="font-heading text-2xl font-semibold text-ink-900"
                    >
                      {c.full_name}
                    </h1>
                    <Badge className={`${meta.cls} border-0`}>
                      {meta.label}
                    </Badge>
                    {c.submission_count > 1 && (
                      <Badge
                        data-testid="returning-candidate-badge"
                        className="bg-amber-100 text-amber-800 border-0"
                      >
                        Returning · {c.submission_count}× applied
                      </Badge>
                    )}
                  </div>
                  <p className="mt-1 text-ink-600">{c.job_category}</p>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-ink-700">
                    <span className="inline-flex items-center gap-1.5">
                      <Phone size={14} /> {c.phone}
                    </span>
                    {c.email && (
                      <span className="inline-flex items-center gap-1.5">
                        <Mail size={14} /> {c.email}
                      </span>
                    )}
                    {c.current_location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={14} /> {c.current_location}
                        {c.current_area ? ` · ${c.current_area}` : ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {(c.cv_path || c.has_cv) && c.cv_path && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    data-testid="download-cv-link"
                    href={fileUrl(c.cv_path)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-50 text-brand-800 text-sm font-medium hover:bg-brand-100"
                  >
                    <FileText size={16} /> View CV
                    <Download size={14} />
                  </a>
                  <button
                    data-testid="generate-cv-pdf-btn"
                    onClick={() => generateCV()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-white text-sm font-medium hover:opacity-90"
                  >
                    <FileText size={16} /> Generate CV (PDF)
                    <Download size={14} />
                  </button>
                  <button
                    data-testid="generate-employer-copy-btn"
                    onClick={downloadEmployerCopy}
                    title="Anonymized PDF for sharing with employers — phone & email hidden"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
                  >
                    <UserX size={16} /> Employer Copy (No Contact)
                    <Download size={14} />
                  </button>
                  {c.terms_acknowledgement_path && (
                    <a
                      data-testid="download-ack-link"
                      href={fileUrl(c.terms_acknowledgement_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-ink-800 text-sm font-medium hover:bg-slate-200"
                    >
                      <FileText size={16} /> T&amp;C Signed
                      <Download size={14} />
                    </a>
                  )}
                </div>
              )}
              {!c.cv_path && (
                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    data-testid="generate-cv-pdf-btn"
                    onClick={() => generateCV()}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary text-white text-sm font-medium hover:opacity-90"
                  >
                    <FileText size={16} /> Generate CV (PDF)
                    <Download size={14} />
                  </button>
                  <button
                    data-testid="generate-employer-copy-btn"
                    onClick={downloadEmployerCopy}
                    title="Anonymized PDF for sharing with employers — phone & email hidden"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
                  >
                    <UserX size={16} /> Employer Copy (No Contact)
                    <Download size={14} />
                  </button>
                  {c.terms_acknowledgement_path && (
                    <a
                      data-testid="download-ack-link"
                      href={fileUrl(c.terms_acknowledgement_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 text-ink-800 text-sm font-medium hover:bg-slate-200"
                    >
                      <FileText size={16} /> T&amp;C Signed
                      <Download size={14} />
                    </a>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details */}
          <Card className="tj-card">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold text-ink-900 mb-2">
                Personal
              </h2>
              <InfoRow label="Father's Name" value={c.father_name} />
              <InfoRow label="Gender" value={c.gender} />
              <InfoRow label="Date of Birth" value={c.date_of_birth} />
              <InfoRow label="Age" value={c.age != null ? `${c.age} years` : null} />
              <InfoRow label="Marital Status" value={c.marital_status} />
              <InfoRow label="WhatsApp" value={c.whatsapp} />
              <InfoRow label="Current Area" value={c.current_area} />
              <InfoRow
                label="Address"
                value={[c.address, c.city, c.state, c.pincode].filter(Boolean).join(", ")}
              />
              <InfoRow
                label="Preferred Locations"
                value={(c.preferred_locations || []).join(", ")}
              />
              <InfoRow
                label="T&C Accepted"
                value={
                  c.terms_accepted_at
                    ? new Date(c.terms_accepted_at).toLocaleString()
                    : null
                }
              />
            </CardContent>
          </Card>

          <Card className="tj-card">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold text-ink-900 mb-2 flex items-center gap-2">
                <GraduationCap size={18} className="text-brand-700" /> Education
              </h2>
              <InfoRow label="Highest Qualification" value={c.highest_qualification} />
              <InfoRow label="Institute" value={c.institute} />
              <InfoRow label="Passing Year" value={c.passing_year} />
              <InfoRow label="Percentage / CGPA" value={c.percentage} />
            </CardContent>
          </Card>

          <Card className="tj-card">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold text-ink-900 mb-2 flex items-center gap-2">
                <Briefcase size={18} className="text-brand-700" /> Experience & Compensation
              </h2>
              <InfoRow
                label="Total Experience"
                value={c.total_experience != null ? `${c.total_experience} years` : null}
              />
              <InfoRow label="Current Company" value={c.current_company} />
              <InfoRow label="Current Designation" value={c.current_designation} />
              <InfoRow
                label="Current Salary"
                value={c.current_salary != null ? `₹${Number(c.current_salary).toLocaleString("en-IN")} / month` : null}
              />
              <InfoRow
                label="Expected Salary"
                value={c.expected_salary != null ? `₹${Number(c.expected_salary).toLocaleString("en-IN")} / month` : null}
              />
              <InfoRow label="Notice Period" value={c.notice_period} />
              <InfoRow label="Industry Preference" value={c.industry_preference} />
              <InfoRow label="English Level" value={c.english_level} />
            </CardContent>
          </Card>

          {(c.skills?.length || c.languages?.length || c.summary) && (
            <Card className="tj-card">
              <CardContent className="p-6">
                {c.summary && (
                  <>
                    <h2 className="font-heading text-lg font-semibold text-ink-900 mb-2">
                      Summary
                    </h2>
                    <p className="text-sm text-ink-700 leading-relaxed mb-5">
                      {c.summary}
                    </p>
                  </>
                )}
                {c.skills?.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2 text-ink-800 font-medium">
                      <Wrench size={16} className="text-brand-700" /> Skills
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.skills.map((s) => (
                        <span
                          key={s}
                          className="tj-pill bg-brand-50 text-brand-800"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {c.languages?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2 text-ink-800 font-medium">
                      <Languages size={16} className="text-brand-700" /> Languages
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {c.languages.map((l) => (
                        <span
                          key={l}
                          className="tj-pill bg-slate-100 text-ink-700"
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right rail – actions */}
        <div className="space-y-6">
          <Card className="tj-card">
            <CardContent className="p-5 space-y-4">
              <div>
                <h3 className="font-heading font-semibold text-ink-900 mb-2">Status</h3>
                <Select value={c.status} onValueChange={updateStatus}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-ink-900 mb-2 flex items-center gap-2">
                  <Building2 size={16} className="text-brand-700" /> Line up
                </h3>
                <Label className="text-xs">Company Name</Label>
                <Input
                  data-testid="lined-up-for-input"
                  value={linedUp}
                  onChange={(e) => setLinedUp(e.target.value)}
                  placeholder="e.g. Infosys Ltd."
                  className="mb-2"
                />
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea
                  data-testid="lined-up-note-input"
                  rows={3}
                  value={linedUpNote}
                  onChange={(e) => setLinedUpNote(e.target.value)}
                  placeholder="Interview round, position, comments…"
                />
                <Button
                  data-testid="save-lined-up-btn"
                  onClick={saveLinedUp}
                  disabled={saving || !linedUp.trim()}
                  className="w-full mt-3 bg-brand-700 hover:bg-brand-800 text-white"
                >
                  Save line-up
                </Button>
                {c.lined_up_for && (
                  <p className="text-xs text-ink-600 mt-2">
                    Currently lined up for{" "}
                    <span className="font-medium text-ink-900">{c.lined_up_for}</span>
                    {c.lined_up_by && ` by ${c.lined_up_by}`}
                  </p>
                )}
              </div>

              <div>
                <h3 className="font-heading font-semibold text-ink-900 mb-2">
                  Internal note
                </h3>
                <Textarea
                  data-testid="internal-note-input"
                  rows={3}
                  value={internalNote}
                  onChange={(e) => setInternalNote(e.target.value)}
                  placeholder="Visible to the whole team"
                />
                <Button
                  data-testid="save-note-btn"
                  onClick={saveNote}
                  variant="outline"
                  className="w-full mt-2 border-brand-200 text-brand-700 hover:bg-brand-50"
                >
                  Save note
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="tj-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-ink-900 flex items-center gap-2">
                  <CalendarDays size={16} className="text-brand-700" /> Interviews
                </h3>
                <Button
                  data-testid="schedule-interview-btn"
                  size="sm"
                  onClick={() => setIvOpen(true)}
                  className="bg-brand-700 hover:bg-brand-800 text-white"
                >
                  <Plus size={14} className="mr-1" /> Schedule
                </Button>
              </div>
              {interviews.length === 0 ? (
                <p className="text-sm text-ink-600">No interviews scheduled.</p>
              ) : (
                <ul className="space-y-3" data-testid="interview-list">
                  {interviews.map((iv) => (
                    <li
                      key={iv.id}
                      className="rounded-lg border border-slate-200 p-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-ink-900 truncate">
                            {iv.company_name}
                            {iv.role && (
                              <span className="text-ink-600"> · {iv.role}</span>
                            )}
                          </div>
                          <div className="text-xs text-ink-600 mt-0.5">
                            {new Date(iv.scheduled_at).toLocaleString()}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            <Badge className="bg-brand-50 text-brand-800 border-0 text-[10px]">
                              {iv.status.replace("_", " ")}
                            </Badge>
                            <Badge className="bg-slate-100 text-ink-700 border-0 text-[10px]">
                              {iv.mode}
                            </Badge>
                          </div>
                          {iv.notes && (
                            <p className="text-xs text-ink-600 italic mt-1">
                              “{iv.notes}”
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => removeInterview(iv.id)}
                          data-testid={`remove-iv-${iv.id}`}
                          className="text-xs text-rose-700 hover:underline shrink-0"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {(c.payments || []).length > 0 && (
            <Card className="tj-card">
              <CardContent className="p-5">
                <h3 className="font-heading font-semibold text-ink-900 mb-3 flex items-center gap-2">
                  <IndianRupee size={16} className="text-brand-700" /> Payments
                </h3>
                <ul className="space-y-3" data-testid="payments-list">
                  {(c.payments || []).map((p, idx) => (
                    <li
                      key={`${p.utr}-${idx}`}
                      className="rounded-lg border border-slate-200 p-3"
                      data-testid={`payment-item-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-ink-900">
                            ₹{p.amount_inr} · {(p.method || "upi").toUpperCase()}
                          </div>
                          <div className="text-xs text-ink-600 mt-0.5 break-all">
                            UTR: <span className="font-mono">{p.utr}</span>
                          </div>
                          <div className="text-xs text-ink-600">
                            {p.at ? new Date(p.at).toLocaleString() : ""}
                          </div>
                          {p.screenshot_path && (
                            <a
                              href={fileUrl(p.screenshot_path)}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`payment-screenshot-${idx}`}
                              className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline mt-1"
                            >
                              <Eye size={12} /> View screenshot
                            </a>
                          )}
                          {p.verified && p.verified_by && (
                            <p className="text-[11px] text-emerald-700 mt-1">
                              Verified by {p.verified_by}
                              {p.verified_at
                                ? ` · ${new Date(p.verified_at).toLocaleString()}`
                                : ""}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 flex flex-col items-end gap-2">
                          {p.verified ? (
                            <Badge
                              data-testid={`payment-verified-badge-${idx}`}
                              className="bg-emerald-100 text-emerald-800 border-0"
                            >
                              <BadgeCheck size={12} className="mr-1" /> Verified
                            </Badge>
                          ) : (
                            <Badge
                              data-testid={`payment-pending-badge-${idx}`}
                              className="bg-amber-100 text-amber-800 border-0"
                            >
                              <Clock size={12} className="mr-1" /> Pending
                            </Badge>
                          )}
                          {user?.role === "admin" && (
                            <Button
                              size="sm"
                              variant={p.verified ? "outline" : "default"}
                              data-testid={`verify-payment-btn-${idx}`}
                              onClick={() => toggleVerifyPayment(p)}
                              className={
                                p.verified
                                  ? "border-amber-200 text-amber-700 hover:bg-amber-50 h-7 text-xs"
                                  : "bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                              }
                            >
                              {p.verified ? "Unverify" : "Verify"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <Card className="tj-card">
            <CardContent className="p-5">
              <h3 className="font-heading font-semibold text-ink-900 mb-3 flex items-center gap-2">
                <Eye size={16} className="text-brand-700" /> Seen by ({c.seen_by?.length || 0})
              </h3>
              {(c.seen_by || []).length === 0 ? (
                <p className="text-sm text-ink-600">Not viewed yet.</p>
              ) : (
                <ul className="space-y-2" data-testid="seen-by-list">
                  {(c.seen_by || []).slice().reverse().map((s) => (
                    <li
                      key={`${s.user_id}-${s.at}`}
                      className="flex items-center justify-between text-sm"
                    >
                      <span className="text-ink-800 font-medium">
                        {s.user_name}
                      </span>
                      <span className="text-xs text-ink-600">
                        {new Date(s.at).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {user?.role === "admin" && (
            <Button
              onClick={deleteCandidate}
              variant="outline"
              data-testid="delete-candidate-btn"
              className="w-full border-rose-200 text-rose-700 hover:bg-rose-50"
            >
              <Trash2 size={14} className="mr-2" /> Delete candidate
            </Button>
          )}
        </div>
      </div>

      <Dialog open={ivOpen} onOpenChange={setIvOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule an interview</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Company *</Label>
              <Input
                data-testid="iv-company-input"
                value={ivForm.company_name}
                onChange={(e) =>
                  setIvForm({ ...ivForm, company_name: e.target.value })
                }
                placeholder="e.g. Bharat Tirtha Darshan"
              />
            </div>
            <div>
              <Label>Role / Position</Label>
              <Input
                data-testid="iv-role-input"
                value={ivForm.role}
                onChange={(e) => setIvForm({ ...ivForm, role: e.target.value })}
                placeholder="e.g. Telecaller"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date *</Label>
                <Input
                  data-testid="iv-date-input"
                  type="date"
                  value={ivForm.date}
                  onChange={(e) =>
                    setIvForm({ ...ivForm, date: e.target.value })
                  }
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input
                  data-testid="iv-time-input"
                  type="time"
                  value={ivForm.time}
                  onChange={(e) =>
                    setIvForm({ ...ivForm, time: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <Label>Mode</Label>
              <Select
                value={ivForm.mode}
                onValueChange={(v) => setIvForm({ ...ivForm, mode: v })}
              >
                <SelectTrigger data-testid="iv-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["In-person", "Phone", "Video", "WhatsApp"].map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location / Link</Label>
              <Input
                data-testid="iv-location-input"
                value={ivForm.location}
                onChange={(e) =>
                  setIvForm({ ...ivForm, location: e.target.value })
                }
                placeholder="Office address or video link"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="iv-notes-input"
                rows={2}
                value={ivForm.notes}
                onChange={(e) =>
                  setIvForm({ ...ivForm, notes: e.target.value })
                }
                placeholder="Round, panel, salary discussed…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIvOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="iv-save-btn"
              onClick={scheduleInterview}
              className="bg-brand-700 hover:bg-brand-800 text-white"
            >
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
